import { HttpRequest, HttpResponse } from "@angular/common/http";

/**
 * environments
 */
import { environment } from "src/environments/environment";

/**
 * utilities
 */
import { AppHttpUtils } from '../../../../../utilities';

/**
 * services
 */
import { ILayoutLang, LayoutLang } from "@core/services";

/**
 * App Request Utilities
 * @class AppRequestUtils
 * @description Provides utility methods for app-specific HTTP request modifications.
 * Contains static methods for adding app host URLs, language headers, and response logging.
 * Used by HTTP interceptors to enhance requests with app-specific configurations.
 * 
 * @example
 * ```typescript
 * // Add app host URL
 * const enhancedReq = AppRequestUtils.addAppHostUrl(request);
 * 
 * // Add language header
 * const langReq = AppRequestUtils.addAcceptLanguageHeader(request);
 * 
 * // Log response
 * AppRequestUtils.logResponse(request, response);
 * ```
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
export class AppRequestUtils {

    /**
     * Adds the app host URL to HTTP requests
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to modify
     * @returns {HttpRequest<unknown>} Modified request with app host URL
     * @description Prepends the app endpoint URL to requests that should be processed by app interceptors.
     * Only modifies requests that pass the app request validation check.
     * 
     * @example
     * ```typescript
     * // Original request: /api/users
     * // Enhanced request: https://api.example.com/api/users
     * const enhancedReq = AppRequestUtils.addAppHostUrl(request);
     * ```
     */
    public static addAppHostUrl(req: HttpRequest<unknown>): HttpRequest<unknown> {

        const _isHasBypassAppRequest = AppHttpUtils.isHasBypassAppRequest(req);

        // Check if the request is not an app request
        // If the request is not an app request, return the request
        if (_isHasBypassAppRequest) return req;

        // If the request is not an app request, add the app host url
        return req.clone({
            // Add the app host url to the request
            url: `${environment.APP_ENDPOINT}${req.url}`
        });

    }

    /**
     * Adds Accept-Language header to HTTP requests
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to modify
     * @returns {HttpRequest<unknown>} Modified request with language header
     * @description Injects the current application language into the Accept-Language header.
     * Uses the LangService to get the current language and falls back to English if not available.
     * 
     * @example
     * ```typescript
     * // Adds header: Accept-Language: en-US
     * const langReq = AppRequestUtils.addAcceptLanguageHeader(request);
     * ```
     */
    public static addAcceptLanguageHeader(req: HttpRequest<unknown>, lang: ILayoutLang): HttpRequest<unknown> {

        // Get current language from language service
        const currentLang = lang;

        return req.clone({
            setHeaders: {
                'Accept-Language': currentLang || LayoutLang.En
            }
        });

    }

    /**
     * Logs HTTP response details to console
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The original HTTP request
     * @param {HttpResponse<unknown>} event - The HTTP response event
     * @returns {void}
     * @description Outputs detailed response information to console for debugging purposes.
     * Includes request URL, method, response status, headers, and body data.
     * Should only be used in development environment.
     * 
     * @example
     * ```typescript
     * // Logs response details to console
     * AppRequestUtils.logResponse(request, response);
     * // Output: 🔵 HTTP Response: { url: '/api/users', method: 'GET', status: 200, ... }
     * ```
     */
    public static logResponse(req: HttpRequest<unknown>, event: HttpResponse<unknown>): void {
        console.log('🔵 HTTP Response:', {
            url: req.url,
            method: req.method,
            status: event.status,
            statusText: event.statusText,
            headers: Object.fromEntries(event.headers.keys().map(key => [key, event.headers.get(key)])),
            body: event.body
        });
    }

}